import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, UserPlus, Loader2, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Cadastro rápido de Fornecedor/Cliente — abre por cima do lançamento sem perder o contexto. */
export const QuickContactModal = ({ isOpen, onClose, onCreated, defaultKind = "fornecedor", members = [] }) => {
  const [name, setName] = useState("");
  const [kind, setKind] = useState(defaultKind);
  const [document, setDocument] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState("");

  useEffect(() => {
    if (isOpen) {
      setName("");
      setKind(defaultKind);
      setDocument("");
      setEmail("");
      setPhone("");
      setSelectedMemberId("");
    }
  }, [isOpen, defaultKind]);

  if (!isOpen) return null;

  const submit = async () => {
    if (kind === "irmao") {
      const member = members.find((item) => item.id === selectedMemberId);
      if (!member) {
        toast.error("Selecione um Irmão.");
        return;
      }
      if (onCreated) onCreated({ ...member, entity_type: "member", kind: "irmao" });
      toast.success("Irmão selecionado.");
      onClose();
      return;
    }
    if (!name.trim()) {
      toast.error("Informe o nome do fornecedor/cliente.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contacts`, {
        name: name.trim(),
        kind,
        document: document.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      toast.success(`${kind === "fornecedor" ? "Fornecedor" : "Cliente"} cadastrado.`);
      if (onCreated) onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="quick-contact-modal"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#D4AF37]/20 bg-[#080D18] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs text-[#D4AF37] uppercase tracking-widest font-bold">
              <UserPlus className="w-3.5 h-3.5" />
              <span>Cadastro Rápido</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">Fornecedor, Cliente ou Irmão</h2>
          </div>
          <button
            data-testid="quick-contact-close"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
              Tipo
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setKind("fornecedor")}
                data-testid="quick-contact-kind-fornecedor"
                className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                  kind === "fornecedor"
                    ? "bg-rose-500/20 text-rose-300 border-rose-500/50"
                    : "bg-[#131C2E] text-slate-400 border-slate-700 hover:text-white"
                }`}
              >
                Fornecedor
              </button>
              <button
                type="button"
                onClick={() => setKind("cliente")}
                data-testid="quick-contact-kind-cliente"
                className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                  kind === "cliente"
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50"
                    : "bg-[#131C2E] text-slate-400 border-slate-700 hover:text-white"
                }`}
              >
                Cliente
              </button>
              <button
                type="button"
                onClick={() => setKind("irmao")}
                data-testid="quick-contact-kind-irmao"
                className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                  kind === "irmao"
                    ? "bg-sky-500/20 text-sky-300 border-sky-500/50"
                    : "bg-[#131C2E] text-slate-400 border-slate-700 hover:text-white"
                }`}
              >
                Irmão
              </button>
            </div>
          </div>

          {kind === "irmao" ? (
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
                Selecionar Irmão *
              </label>
              <select
                data-testid="quick-contact-member-select"
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
                className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
              >
                <option value="">— Escolha pelo nome —</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>{member.name} (CIM {member.cim})</option>
                ))}
              </select>
            </div>
          ) : <>
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
              Nome / Razão Social *
            </label>
            <input
              data-testid="quick-contact-name"
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Papelaria Santo Antonio Ltda"
              className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
                CNPJ / CPF
              </label>
              <input
                data-testid="quick-contact-document"
                type="text"
                value={document}
                onChange={(e) => setDocument(e.target.value)}
                placeholder="00.000.000/0000-00"
                className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
                Telefone
              </label>
              <input
                data-testid="quick-contact-phone"
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(00) 00000-0000"
                className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-[#D4AF37] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1.5">
              E-mail
            </label>
            <input
              data-testid="quick-contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contato@fornecedor.com.br"
              className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-[#D4AF37] outline-none"
            />
          </div>
          </>}
        </div>

        <div className="px-6 py-4 border-t border-slate-800/80 bg-[#080D18] flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold"
          >
            Cancelar
          </button>
          <button
            data-testid="quick-contact-save"
            onClick={submit}
            disabled={saving || (kind === "irmao" ? !selectedMemberId : !name.trim())}
            className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Salvando…" : kind === "irmao" ? "Selecionar Irmão" : "Salvar & Selecionar"}
          </button>
        </div>
      </div>
    </div>
  );
};
